/**
 *    SPDX-License-Identifier: Apache-2.0
 */

import React, { Component } from 'react';
import { withStyles } from '@material-ui/core/styles';
import { Button } from 'reactstrap';
import matchSorter from 'match-sorter';
import find from 'lodash/find';
import moment from 'moment';
import { isNull } from 'util';
import ReactTable from '../Styled/Table';
import MultiSelect from '../Styled/MultiSelect';
import DatePicker from '../Styled/DatePicker';
import SearchIcon from '@material-ui/icons/Search';
import {
	blockListSearchType,
	blockRangeSearchType,
	currentChannelType,
	getTransactionType,
	transactionType,
	getTxnListType,
	txnListType
} from '../types';
import { FormHelperText, TablePagination, TextField } from '@mui/material';
import { MenuItem, Select } from '@material-ui/core';
import {
	reg,
	rowsPerPageOptions,
	rangeLimitOptions,
	defaultRangeLimit,
	E001,
	E002,
	E003,
	E004,
	E005
} from './constants';
import { Info } from '@material-ui/icons';

/* istanbul ignore next */
const styles = theme => {
	const { type } = theme.palette;
	const dark = type === 'dark';
	return {
		container: {
			padding: '12px',
			backgroundColor: dark ? '#2a2438' : '#ffffff'
		},
		filterSection: {
			backgroundColor: dark ? '#3c3558' : '#ffffff',
			borderRadius: '4px',
			padding: '12px',
			marginBottom: '12px',
			border: dark ? '1px solid #4a4a4a' : '1px solid #e0e0e0'
		},
		filterTitle: {
			fontSize: '14px',
			fontWeight: '600',
			marginBottom: '12px',
			color: dark ? '#ffffff' : '#333333',
			paddingBottom: '8px',
			borderBottom: `1px solid ${dark ? '#4a4a4a' : '#e0e0e0'}`
		},
		hash: {
			'&, & li, & ul': {
				overflow: 'visible !important'
			}
		},
		blockLink: {
			color: dark ? '#4A90E2' : '#4A90E2',
			textDecoration: 'none',
			fontWeight: '500',
			cursor: 'pointer',
			'&:hover': {
				textDecoration: 'underline',
				color: dark ? '#6bb3ff' : '#2d6fb8'
			}
		},
		transactionLink: {
			color: dark ? '#4A90E2' : '#4A90E2',
			textDecoration: 'none',
			fontWeight: '500',
			cursor: 'pointer',
			'&:hover': {
				textDecoration: 'underline',
				color: dark ? '#6bb3ff' : '#2d6fb8'
			}
		},
		htinputs: {
			display: 'flex',
			marginBottom: '8px',
			position: 'relative'
		},
		errorText: {
			width: '100%',
			position: 'absolute',
			left: '0px',
			bottom: '-20px',
			cursor: 'default'
		},
		startBlock: {
			marginRight: '5px'
		},
		fullHash: {
			display: 'none'
		},
		lastFullHash: {
			display: 'none'
		},
		filter: {
			width: '100%',
			margin: '0px !important',
			display: 'flex',
			alignItems: 'flex-end',
			flexWrap: 'wrap'
		},
		filterButton: {
			width: '100% !important',
			borderRadius: '4px',
			fontWeight: '400',
			padding: '8px 12px',
			fontSize: '13px',
			height: '38px',
			lineHeight: '1.2'
		},
		searchButton: {
			width: '100% !important',
			backgroundColor: dark ? '#4A90E2' : '#4A90E2',
			borderColor: dark ? '#4A90E2' : '#4A90E2',
			borderRadius: '4px',
			fontWeight: '400',
			padding: '8px 12px',
			fontSize: '13px',
			height: '38px',
			lineHeight: '1.2',
			'&:hover': {
				backgroundColor: dark ? '#6bb3ff' : '#2d6fb8'
			},
			'&:disabled': {
				opacity: 0.5,
				cursor: 'not-allowed'
			}
		},
		filterElement: {
			textAlign: 'left',
			display: 'flex',
			flexDirection: 'column',
			padding: '0px 8px !important',
			justifyContent: 'flex-end',
			'& > div': {
				width: '100% !important'
			},
			'& .label': {
				margin: '0 0 6px 0',
				fontWeight: '400',
				fontSize: '12px',
				color: dark ? '#e0e0e0' : '#666666',
				height: '18px',
				lineHeight: '18px',
				display: 'block'
			},
			'& input, & .react-datepicker-wrapper, & .react-datepicker__input-container': {
				width: '100% !important',
				'& input': {
					height: '38px !important',
					padding: '8px 12px !important',
					fontSize: '13px !important',
					borderRadius: '4px !important',
					border: `1px solid ${dark ? '#4a4a4a' : '#d0d0d0'} !important`
				}
			},
			'& .multiselect': {
				minHeight: '38px !important',
				'& .multiselect__tags': {
					minHeight: '38px !important',
					padding: '6px 40px 0 8px !important',
					fontSize: '13px !important',
					borderRadius: '4px !important',
					border: `1px solid ${dark ? '#4a4a4a' : '#d0d0d0'} !important`
				},
				'& .multiselect__placeholder': {
					padding: '8px 0 !important',
					fontSize: '13px !important',
					marginBottom: '0 !important'
				},
				'& .multiselect__single': {
					padding: '8px 0 !important',
					fontSize: '13px !important',
					marginBottom: '0 !important',
					lineHeight: '22px !important'
				}
			}
		},
		filterDate: {
			'& > div': {
				width: '100% !important'
			}
		},
		blockRangeRow: {
			marginBottom: '8px !important',
			marginLeft: '8px !important',
			minWidth: '25vw',
			'& > div': {
				marginRight: '8px'
			},
			'& > p': {
				'white-space': 'nowrap'
			}
		},
		text: {
			alignSelf: 'center',
			marginRight: '8px',
			marginLeft: '8px',
			fontSize: '13px'
		},
		blockrange: {
			'& > div': {
				width: '40% !important'
			}
		},
		iconButton: {
			color: '#21295c',
			alignSelf: 'center'
		},
		tableContainer: {
			backgroundColor: dark ? '#3c3558' : '#ffffff',
			borderRadius: '4px',
			padding: '0',
			border: dark ? '1px solid #4a4a4a' : '1px solid #e0e0e0',
			overflow: 'hidden',
			'& .ReactTable': {
				border: 'none'
			},
			'& .rt-table': {
				border: 'none'
			}
		},
		tableStyles: {
			'& .rt-thead': {
				backgroundColor: '#E0F2F7 !important',
				'& .rt-th': {
					backgroundColor: '#E0F2F7 !important',
					color: '#333333 !important',
					fontWeight: '600',
					fontSize: '13px',
					padding: '12px 16px !important',
					borderRight: '1px solid #d0e0e5 !important',
					borderBottom: 'none !important',
					'&:last-child': {
						borderRight: 'none !important'
					}
				},
				'& .rt-th input': {
					backgroundColor: '#ffffff !important',
					border: '1px solid #d0e0e5 !important'
				}
			},
			'& .rt-tbody': {
				'& .rt-tr-group': {
					borderBottom: 'none !important',
					'&:nth-child(even) .rt-tr': {
						backgroundColor: '#f9f9f9 !important'
					},
					'&:nth-child(odd) .rt-tr': {
						backgroundColor: '#ffffff !important'
					},
					'& .rt-tr:hover': {
						backgroundColor: '#f0f8ff !important'
					},
					'& .rt-td': {
						padding: '12px 16px !important',
						fontSize: '13px',
						borderRight: 'none !important',
						borderBottom: 'none !important',
						textAlign: 'left !important'
					}
				}
			}
		},
		errorMessage: {
			color: '#dc3545',
			fontSize: '12px',
			marginTop: '4px',
			padding: '4px 8px',
			backgroundColor: dark ? 'rgba(220, 53, 69, 0.1)' : 'rgba(220, 53, 69, 0.05)',
			borderRadius: '3px',
			border: `1px solid ${dark ? 'rgba(220, 53, 69, 0.3)' : 'rgba(220, 53, 69, 0.2)'}`
		}
	};
};
const tablePaginationStyle = {
	display: 'flex',
	justifyContent: 'end',
	padding: '0px 15px',
	alignItems: 'baseline',
	'.MuiToolbar-root': {
		alignItems: 'baseline'
	}
};
let timer;
export class Blocks extends Component {
	constructor(props) {
		super(props);
		this.state = {
			err: false,
			search: false,
			to: moment(),
			orgs: [],
			options: [],
			filtered: [],
			sorted: [],
			from: moment().subtract(1, 'days'),
			page: 0,
			rowsPerPage: 10,
			searchClick: false,
			queryFlag: false,
			defaultQuery: true,
			startBlock: '',
			endBlock: '',
			rangeErr: '',
			brs: false,
			rangeLimit: defaultRangeLimit
		};
	}

	componentDidMount() {
		const { blockListSearch } = this.props;
		const selection = {};
		blockListSearch?.forEach(element => {
			selection[element.blocknum] = false;
		});
		const opts = [];
		this.props.transactionByOrg.forEach(val => {
			opts.push({ label: val.creator_msp_id, value: val.creator_msp_id });
		});
		this.setState({ selection, options: opts });
		this.searchBlockList();
	}

	componentDidUpdate(prevProps, prevState) {
		if (
			this.state.search &&
			this.props.currentChannel !== prevProps.currentChannel
		) {
			if (this.interval !== undefined) {
				clearInterval(this.interval);
			}
			this.interval = setInterval(() => {
				this.searchBlockList(this.props.currentChannel);
			}, 60000);
			this.searchBlockList(this.props.currentChannel);
		}
		if (
			(!this.state.brs && prevState.page != this.state.page) ||
			prevState.rowsPerPage != this.state.rowsPerPage ||
			this.state.searchClick
		) {
			this.setState({ searchClick: false });
			this.handleSearch();
		}

		if (prevProps.blockRangeLoaded != this.props.blockRangeLoaded) {
			if (this.props.blockRangeLoaded) {
				if (typeof this.props.blockRangeSearch === 'string') {
					this.setState({ rangeErr: this.props.blockRangeSearch });
				}
			} else {
				if (this.state.rangeErr) this.setState({ rangeErr: '' });
			}
		}
	}

	componentWillUnmount() {
		clearInterval(this.interval);
		clearTimeout(timer);
	}

	handleCustomRender(selected, options) {
		if (selected.length === 0) {
			return 'Select Orgs';
		}
		if (selected.length === options.length) {
			return 'All Orgs Selected';
		}

		return selected.join(',');
	}

	searchBlockList = async channel => {
		let pageParams = { page: this.state.page + 1, size: this.state.rowsPerPage };
		let query = '';
		if (this.state.queryFlag) {
			query = this.state.from
				? `from=${new Date(this.state.from).toString()}&to=${new Date(
						this.state.to
				  ).toString()}`
				: ``;
			for (let i = 0; i < this.state.orgs.length; i++) {
				query += `&orgs=${this.state.orgs[i]}`;
			}
			this.setState({ queryFlag: false });
		} else if (this.state.defaultQuery) {
			query = '';
			this.setState({ defaultQuery: false });
		} else {
			query = this.props.blockListSearchQuery;
		}
		let channelhash = this.props.currentChannel;
		if (channel !== undefined) {
			channelhash = channel;
		}
		await this.props.getBlockListSearch(channelhash, query, pageParams);
	};

	handleTransactionClick = (e, txhash) => {
		e.preventDefault();
		if (txhash) {
			window.open(`#/transactions/${txhash}`, '_blank');
		}
	};

	handleBlockClick = (e, blockHash) => {
		e.preventDefault();
		const { history } = this.props;
		if (history && blockHash) {
			history.push(`/blocks/${blockHash}`);
		}
	};

	handleMultiSelect = value => {
		this.setState({ orgs: value });
	};

	handleSearch = async () => {
		if (this.interval !== undefined) {
			clearInterval(this.interval);
		}
		this.interval = setInterval(() => {
			this.searchBlockList();
		}, 60000);
		await this.searchBlockList();
		this.setState({ search: true });
	};

	searchBlockRange = async channel => {
		let channelhash = this.props.currentChannel;
		if (channel !== undefined) {
			channelhash = channel;
		}
		await this.props.getBlockRangeSearch(
			channelhash,
			this.state.startBlock,
			this.state.endBlock
		);
	};

	handleRangeChange = e => {
		const { name, value } = e.target;
		if (reg.test(value))
			this.setState({
				[name]: value,
				rangeErr: ''
			});
	};
	handleRangeSubmit = e => {
		e.preventDefault();
		if (this.state.endBlock === '' || this.state.startBlock === '') {
			this.setState({ rangeErr: E001 });
			return;
		}
		if (Number(this.state.endBlock) < Number(this.state.startBlock)) {
			console.log('err occured');
			this.setState({
				rangeErr: E002
			});
			return;
		}
		if (this.state.endBlock - this.state.startBlock >= this.state.rangeLimit) {
			if (this.state.rangeLimit < 100) {
				this.setState({ rangeErr: E004(this.state.rangeLimit) });
			} else {
				this.setState({ rangeErr: E003 });
			}
			return;
		}
		this.searchBlockRange();
		this.setState({ search: true, brs: true, page: 0 });
	};
	handleClearSearch = () => {
		if (this.interval !== undefined) {
			clearInterval(this.interval);
		}
		this.setState({
			to: moment(),
			orgs: [],
			err: false,
			from: moment().subtract(1, 'days'),
			startBlock: '',
			endBlock: ''
		});
	};


	handleEye = (row, val) => {
		const { selection } = this.state;
		const data = Object.assign({}, selection, { [row.index]: !val });
		this.setState({ selection: data });
	};
	handlePageChange = (_e, page) => {
		this.setState({ page: page });
	};
	handleRowsChange = e => {
		this.setState({ page: 0, rowsPerPage: e.target.value });
	};

	reactTableSetup = classes => [
		{
			Header: 'Block Number',
			accessor: 'blocknum',
			filterMethod: (filter, rows) =>
				matchSorter(
					rows,
					filter.value,
					{ keys: ['blocknum'] },
					{ threshold: matchSorter.rankings.SIMPLEMATCH }
				),
			filterAll: true,
			width: 150
		},
		{
			Header: 'Channel Name',
			accessor: 'channelname',
			filterMethod: (filter, rows) =>
				matchSorter(
					rows,
					filter.value,
					{ keys: ['channelname'] },
					{ threshold: matchSorter.rankings.SIMPLEMATCH }
				),
			filterAll: true
		},
		{
			Header: 'Number of Tx',
			accessor: 'txcount',
			filterMethod: (filter, rows) =>
				matchSorter(
					rows,
					filter.value,
					{ keys: ['txcount'] },
					{ threshold: matchSorter.rankings.SIMPLEMATCH }
				),
			filterAll: true,
			width: 150
		},
		{
			Header: 'Data Hash',
			accessor: 'datahash',
			className: classes.hash,
			Cell: row => (
				<span>
					{row.value ? `${row.value.slice(0, 8)}...${row.value.slice(-8)}` : '-'}
				</span>
			),
			filterMethod: (filter, rows) =>
				matchSorter(
					rows,
					filter.value,
					{ keys: ['datahash'] },
					{ threshold: matchSorter.rankings.SIMPLEMATCH }
				),
			filterAll: true
		},
		{
			Header: 'Block Hash',
			accessor: 'blockhash',
			className: classes.hash,
			Cell: row => (
				<span>
					<a
						data-command="block-partial-hash"
						className={classes.blockLink}
						onClick={e => this.handleBlockClick(e, row.value)}
						href={`/blocks/${row.value}`}
					>
						{row.value ? `${row.value.slice(0, 8)}...${row.value.slice(-8)}` : '-'}
					</a>
				</span>
			),
			filterMethod: (filter, rows) =>
				matchSorter(
					rows,
					filter.value,
					{ keys: ['blockhash'] },
					{ threshold: matchSorter.rankings.SIMPLEMATCH }
				),
			filterAll: true
		},
		{
			Header: 'Previous Hash',
			accessor: 'prehash',
			className: classes.hash,
			Cell: row => (
				<span>
					<a
						className={classes.blockLink}
						onClick={e => this.handleBlockClick(e, row.value)}
						href={`/blocks/${row.value}`}
					>
						{row.value ? `${row.value.slice(0, 8)}...${row.value.slice(-8)}` : '-'}
					</a>
				</span>
			),
			filterMethod: (filter, rows) =>
				matchSorter(
					rows,
					filter.value,
					{ keys: ['prehash'] },
					{ threshold: matchSorter.rankings.SIMPLEMATCH }
				),
			filterAll: true,
			width: 150
		},
		{
			Header: 'Transactions',
			accessor: 'txhash',
			className: classes.hash,
			Cell: row => (
				<ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
					{!isNull(row.value)
						? row.value.map(tid => (
								<li
									key={tid}
									style={{
										overflow: 'hidden',
										whiteSpace: 'nowrap',
										textOverflow: 'ellipsis',
										marginBottom: '4px'
									}}
								>
									<a
										className={classes.transactionLink}
										onClick={e => this.handleTransactionClick(e, tid)}
										href={`#/transactions/${tid}`}
										target="_blank"
										rel="noopener noreferrer"
									>
										{tid ? `${tid.slice(0, 8)}...${tid.slice(-8)}` : '-'}
									</a>
								</li>
						  ))
						: 'null'}
				</ul>
			),
			filterMethod: (filter, rows) =>
				matchSorter(
					rows,
					filter.value,
					{ keys: ['txhash'] },
					{ threshold: matchSorter.rankings.SIMPLEMATCH }
				),
			filterAll: true
		},
		{
			Header: 'Size(KB)',
			accessor: 'blksize',
			filterMethod: (filter, rows) =>
				matchSorter(
					rows,
					filter.value,
					{ keys: ['blksize'] },
					{ threshold: matchSorter.rankings.SIMPLEMATCH }
				),
			filterAll: true,
			width: 150
		}
	];

	render() {
		const reversedBlockRangeList =
			typeof this.props.blockRangeSearch !== 'string' &&
			this.props.blockRangeLoaded
				? this.props.blockRangeSearch
						.slice()
						.sort()
						.reverse()
				: [];
		const blockList = this.state.brs
			? reversedBlockRangeList.slice(
					this.state.page * this.state.rowsPerPage,
					(this.state.page + 1) * this.state.rowsPerPage
			  )
			: this.props.blockListSearch;
		const noOfPages = this.state.brs
			? typeof this.props.blockRangeSearch !== 'string' &&
			  this.props.blockRangeLoaded &&
			  Math.ceil(this.props.blockRangeSearch.length / this.state.rowsPerPage)
			: this.props.blockListSearchTotalPages;
		const { transaction, txnList, classes } = this.props;
		return (
			<div className={classes.container}>
				<div className={classes.filterSection}>
					<div className={classes.filterTitle}>BLOCK LIST</div>
					<div className={`${classes.filter} row searchRow`}>
					<div className={`${classes.filterElement} ${classes.filterDate} col-md-3`}>
						<label className="label">From</label>
						<DatePicker
							id="from"
							selected={this.state.from}
							showTimeSelect
							timeIntervals={5}
							maxDate={this.state.to}
							dateFormat="LLL"
							popperPlacement="bottom"
							popperModifiers={{
								flip: { behavior: ['bottom'] },
								preventOverflow: { enabled: false },
								hide: { enabled: false }
							}}
							onChange={date => {
								if (date > this.state.to) {
									this.setState({ err: true, from: date });
								} else {
									this.setState({ from: date, err: false });
								}
							}}
						/>
					</div>
					<div className={`${classes.filterElement} ${classes.filterDate} col-md-3`}>
						<label className="label">To</label>
						<DatePicker
							id="to"
							selected={this.state.to}
							showTimeSelect
							timeIntervals={5}
							dateFormat="LLL"
							minDate={this.state.from}
							popperPlacement="bottom"
							popperModifiers={{
								flip: { behavior: ['bottom'] },
								preventOverflow: { enabled: false },
								hide: { enabled: false }
							}}
							onChange={date => {
								if (date < this.state.from) {
									this.setState({ err: true, to: date });
								} else {
									this.setState({ to: date, err: false });
								}
							}}
						>
							{this.state.err && (
								<div className={classes.errorMessage}>
									From date should be less than To date
								</div>
							)}
						</DatePicker>
					</div>
					<div className={`${classes.filterElement} col-md-2`}>
						<label className="label">Select Orgs</label>
						<MultiSelect
							hasSelectAll
							valueRenderer={this.handleCustomRender}
							shouldToggleOnHover={false}
							selected={this.state.orgs}
							options={this.state.options}
							selectAllLabel="All Orgs"
							onSelectedChanged={value => {
								this.handleMultiSelect(value);
							}}
						/>
					</div>
					<div className={`${classes.filterElement} col-md-2`}>
						<label className="label" style={{ height: '18px' }}>&nbsp;</label>
						<Button
							className={classes.searchButton}
							color="primary"
							disabled={this.state.err || !this.state.from != !this.state.to}
							onClick={() => {
								this.setState({
									page: 0,
									searchClick: true,
									queryFlag: true,
									defaultQuery: false,
									brs: false
								});
							}}
						>
							Search
						</Button>
					</div>
					<div className={`${classes.filterElement} col-md-1`}>
						<label className="label" style={{ height: '18px' }}>&nbsp;</label>
						<Button
							className={classes.filterButton}
							color="secondary"
							onClick={() => {
								this.handleClearSearch();
							}}
						>
							Reset
						</Button>
					</div>
					<div className={`${classes.filterElement} col-md-1`}>
						<label className="label" style={{ height: '18px' }}>&nbsp;</label>
						<Button
							className={classes.filterButton}
							color="secondary"
							onClick={() => this.setState({ filtered: [], sorted: [] })}
						>
							Clear Filter
						</Button>
					</div>
					<div className={`${classes.filterElement} col-md-4`} style={{ flexDirection: 'row', alignItems: 'center', gap: '8px' }}>
						<label className="label" style={{ margin: 0, height: 'auto', width: 'auto' }}>
							Block No:
						</label>
						<div className={classes.htinputs} style={{ flex: 1, marginBottom: 0 }}>
							<TextField
								type="text"
								name="startBlock"
								className={classes.startBlock}
								id="startBlock"
								style={{ marginRight: '5px', flex: 1 }}
								value={this.state.startBlock}
								onChange={e => this.handleRangeChange(e)}
								variant="standard"
								InputLabelProps={{ shrink: true }}
								label="From"
								error={Boolean(this.state.rangeErr)}
								size="small"
							/>
							<TextField
								type="text"
								name="endBlock"
								id="endBlock"
								style={{ flex: 1 }}
								value={this.state.endBlock}
								onChange={e => this.handleRangeChange(e)}
								variant="standard"
								InputLabelProps={{ shrink: true }}
								label="To"
								error={Boolean(this.state.rangeErr)}
								size="small"
							/>
							<div
								id="blockRangeSearchIcon"
								className={classes.iconButton}
								type="submit"
								onClick={e => this.handleRangeSubmit(e)}
								style={{ marginLeft: '8px' }}
							>
								<SearchIcon />
							</div>
						</div>
						{this.state.rangeErr && (
							<div className={classes.errorMessage} style={{ position: 'absolute', bottom: '-25px', left: '8px', right: '8px' }}>
								{this.state.rangeErr}
							</div>
						)}
					</div>
				</div>
				</div>
				<div className={`${classes.tableContainer} ${classes.tableStyles}`}>
				<ReactTable
					data={blockList || []}
					columns={this.reactTableSetup(classes)}
					pageSize={
						this.state.brs
							? this.props.blockRangeLoaded &&
							  typeof this.props.blockRangeSearch !== 'string' &&
							  this.props.blockRangeSearch.length
							: this.state.rowsPerPage
					}
					list
					filterable
					sorted={this.state.sorted}
					onSortedChange={sorted => {
						this.setState({ sorted });
					}}
					filtered={this.state.filtered}
					onFilteredChange={filtered => {
						this.setState({ filtered });
					}}
					minRows={0}
					style={{ height: '600px' }}
					showPaginationBottom={false}
				/>
				{blockList?.length > 0 && (
					<TablePagination
						page={this.state.page}
						sx={tablePaginationStyle}
						rowsPerPage={this.state.rowsPerPage}
						labelDisplayedRows={() => `Page ${this.state.page + 1} of ${noOfPages}`}
						rowsPerPageOptions={rowsPerPageOptions}
						onRowsPerPageChange={this.handleRowsChange}
						onPageChange={this.handlePageChange}
						backIconButtonProps={{
							disabled: this.state.page === 0
						}}
						nextIconButtonProps={{
							disabled: this.state.page + 1 === noOfPages
						}}
						className={classes.tablePagination}
						labelRowsPerPage={'Items per page'}
					/>
				)}
				</div>
			</div>
		);
	}
}

Blocks.propTypes = {
	blockRangeSearch: blockRangeSearchType.isRequired,
	blockListSearch: blockListSearchType.isRequired,
	currentChannel: currentChannelType.isRequired,
	getTransaction: getTransactionType.isRequired,
	transaction: transactionType,
	txnList: txnListType,
	getTxnList: getTxnListType
};

Blocks.defaultProps = {
	transaction: null,
	txnList: null
};

export default withStyles(styles)(Blocks);
